import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import DealPipeline from './components/DealPipeline';
import DealForm from './components/DealForm';
import ExtractionReview from './components/ExtractionReview';
import ModelPopulator from './components/ModelPopulator';
import Underwriting from './components/Underwriting';
import CompSearch from './components/CompSearch';
import DocumentGen from './components/DocumentGen';
import BuyerQA from './components/BuyerQA';
import BorrowerDiligence from './components/BorrowerDiligence';
import ThirdPartyReports from './components/ThirdPartyReports';
import TemplateManager from './components/TemplateManager';
import LLMSettings from './components/LLMSettings';
import DealNotes from './components/DealNotes';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

export const COLORS = {
  navy: '#0F1B2D', navyLight: '#1A2A42', gold: '#C9A84C', goldLight: '#E8D59A',
  bg: '#F0F2F5', white: '#FFFFFF', border: '#DEE2E6', text: '#333333', textLight: '#6C757D',
  success: '#28A745', danger: '#DC3545', warning: '#FFC107',
};

function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(r => r.json());
}

export default function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('pipeline');
  const [activeDeal, setActiveDeal] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('cmbs_token');
    if (token) {
      apiFetch('/auth/me').then(res => { if (res.success) setUser(res.data); else localStorage.removeItem('cmbs_token'); setLoading(false); }).catch(() => { localStorage.removeItem('cmbs_token'); setLoading(false); });
    } else { setLoading(false); }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginError('');
    try {
      const res = await apiFetch('/auth/login', { method: 'POST', body: { email: loginForm.email, password: loginForm.password } });
      if (res.success) { localStorage.setItem('cmbs_token', res.data.token); setUser(res.data.user); }
      else setLoginError(res.error || 'Login failed');
    } catch (err) { setLoginError('Login failed'); }
  };

  const handleLogout = () => { localStorage.removeItem('cmbs_token'); setUser(null); setActiveDeal(null); setView('pipeline'); };

  const openDeal = async (dealId) => {
    const res = await apiFetch(`/deals/${dealId}`);
    if (res.success) {
      setActiveDeal(res.data);
      const p = res.data.progress || {};
      if (p.hasDocuments) setView('documents');
      else if (p.hasComps) setView('comps');
      else if (p.hasUnderwriting) setView('underwriting');
      else if (p.hasPopulatedModel) setView('populate');
      else if (p.hasExtraction && p.approvedFieldCount > 0) setView('populate');
      else if (p.hasFiles) setView('extraction');
      else setView('intake');
    }
  };

  const refreshDeal = async () => {
    if (activeDeal) { const res = await apiFetch(`/deals/${activeDeal.id}`); if (res.success) setActiveDeal(res.data); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: COLORS.bg }}><div style={{ color: COLORS.navy }}>Loading...</div></div>;

  if (!user) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: COLORS.navy }}>
      <form onSubmit={handleLogin} style={{ background: COLORS.white, padding: 40, borderRadius: 8, width: 380, boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
        <img src="/tranchedesk-logo.jpg" alt="TrancheDesk" style={{ display: 'block', margin: '0 auto 16px', maxWidth: 200, borderRadius: 6 }} />
        <h2 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 8, textAlign: 'center' }}>TrancheDesk</h2>
        <p style={{ color: COLORS.textLight, textAlign: 'center', marginBottom: 24, fontSize: 14 }}>Sign in to continue</p>
        {loginError && <div style={{ background: '#FDE8E8', color: COLORS.danger, padding: '8px 12px', borderRadius: 4, marginBottom: 16, fontSize: 13 }}>{loginError}</div>}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: COLORS.textLight }}>Email</label>
          <input type="email" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 14 }} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: COLORS.textLight }}>Password</label>
          <input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 14 }} />
        </div>
        <button type="submit" style={{ width: '100%', padding: '12px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Sign In</button>
      </form>
    </div>
  );

  const renderContent = () => {
    if (view === 'pipeline') return <DealPipeline onOpenDeal={openDeal} onNewDeal={() => setView('new-deal')} />;
    if (view === 'new-deal') return <DealForm onCreated={deal => { setActiveDeal(deal); setView('extraction'); }} onCancel={() => setView('pipeline')} />;
    if (view === 'templates') return <TemplateManager />;
    if (view === 'llm-settings') return <LLMSettings />;
    if (!activeDeal) return <DealPipeline onOpenDeal={openDeal} onNewDeal={() => setView('new-deal')} />;
    switch (view) {
      case 'intake': return <DealForm deal={activeDeal} onCreated={refreshDeal} onCancel={() => setView('pipeline')} />;
      case 'extraction': return <ExtractionReview deal={activeDeal} onUpdate={refreshDeal} />;
      case 'populate': return <ModelPopulator deal={activeDeal} onUpdate={refreshDeal} />;
      case 'underwriting': return <Underwriting deal={activeDeal} onUpdate={refreshDeal} />;
      case 'comps': return <CompSearch deal={activeDeal} onUpdate={refreshDeal} />;
      case 'documents': return <DocumentGen deal={activeDeal} onUpdate={refreshDeal} onNavigate={setView} />;
      case 'borrower_diligence': return <BorrowerDiligence deal={activeDeal} onUpdate={refreshDeal} />;
      case 'third_party': return <ThirdPartyReports deal={activeDeal} onUpdate={refreshDeal} />;
      case 'buyerqa': return <BuyerQA deal={activeDeal} onUpdate={refreshDeal} />;
      case 'deal_notes': return <DealNotes deal={activeDeal} token={localStorage.getItem('cmbs_token')} />;
      default: return <DealPipeline onOpenDeal={openDeal} onNewDeal={() => setView('new-deal')} />;
    }
  };

  return (
    <Layout user={user} view={view} activeDeal={activeDeal} onNavigate={setView} onLogout={handleLogout}
      onBackToPipeline={() => { setActiveDeal(null); setView('pipeline'); }}>
      {renderContent()}
    </Layout>
  );
}
